import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, GoogleButton } from "@/pages/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [provinces, setProvinces] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", province: "", city: "", preferred_language: "Francais" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/provinces").then((r) => setProvinces(r.data));
    api.get("/languages").then((r) => setLanguages(r.data));
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(f);
      toast.success(t("auth.register_title") + " ✓");
      navigate("/");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.register_title")} subtitle={t("auth.register_sub")}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder={t("auth.first_name")} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="reg-firstname" />
          <Input placeholder={t("auth.last_name")} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="reg-lastname" />
        </div>
        <Input type="email" placeholder={t("auth.email")} value={f.email} onChange={(e) => set("email", e.target.value)} required data-testid="reg-email" />
        <Input placeholder="Téléphone (+243…)" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="reg-phone" />
        <Input type="password" placeholder={t("auth.password")} value={f.password} onChange={(e) => set("password", e.target.value)} required data-testid="reg-password" />
        <div className="grid grid-cols-2 gap-3">
          <Select value={f.province} onValueChange={(v) => set("province", v)}>
            <SelectTrigger data-testid="reg-province"><SelectValue placeholder={t("browse.province")} /></SelectTrigger>
            <SelectContent>{provinces.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Ville" value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="reg-city" />
        </div>
        <Select value={f.preferred_language} onValueChange={(v) => set("preferred_language", v)}>
          <SelectTrigger data-testid="reg-language"><SelectValue placeholder={t("browse.language")} /></SelectTrigger>
          <SelectContent>{languages.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="submit" disabled={loading} className="w-full h-11 font-semibold" data-testid="reg-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.register_btn")}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-4"><span className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">{t("auth.or")}</span><span className="flex-1 h-px bg-border" /></div>
      <GoogleButton label={t("auth.google")} />
      <p className="text-sm text-center text-muted-foreground mt-5">{t("auth.have_account")} <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="goto-login">{t("auth.login_link")}</Link></p>
    </AuthShell>
  );
}
